import React, { type FC, useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';

import { useSnackbar } from '../../../../shared/hooks/useSnackbar';
import { appRoutes } from '../../../../shared/store';
import Div from '../../../../shared/ui/Div';
import { usePutLingoLiftStudentLessonAssignmentCompletionMutation } from '../../../../entities/lingoLiftLesson';
import styles from './styles';

import { COMPLETION_STATES, type CompletionState, type CompletionError } from './types';

const REDIRECT_TIMEOUT = 3000;

interface ILingoLiftLessonFooterProps {
  assignmentCompletionId?: number;
  state?: CompletionState;
}

const LingoLiftLessonFooter: FC<ILingoLiftLessonFooterProps> = ({
  assignmentCompletionId,
  state,
}) => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [onSubmitCompletion, { isSuccess, error, isLoading }] =
    usePutLingoLiftStudentLessonAssignmentCompletionMutation();

  const { showSuccessNotification, showErrorNotification } = useSnackbar();

  const handleSubmit = async () => {
    if (!assignmentCompletionId || isLoading || isSuccess) {
      return;
    }

    try {
      await onSubmitCompletion({
        id: assignmentCompletionId,
        stateAction: 'complete',
      }).unwrap();
      showSuccessNotification('Assignment completed successfully');
    } catch (err) {
      const error = err as CompletionError;
      showErrorNotification(error.message || 'Failed to complete assignment');
    }
  };

  useEffect(() => {
    if (isSuccess) {
      setIsRedirecting(true);
      const timer = setTimeout(() => {
        window.location.href = appRoutes.studentDashboard;
      }, REDIRECT_TIMEOUT);

      return () => clearTimeout(timer);
    }
  }, [isSuccess]);

  const isButtonDisabled = isLoading || isSuccess || state === COMPLETION_STATES.COMPLETED;

  if (state === COMPLETION_STATES.COMPLETED) {
    return null;
  }

  return (
    <Div sx={styles.wrapper}>
      <Button
        onClick={handleSubmit}
        variant="contained"
        disabled={isButtonDisabled}
        startIcon={isLoading && <CircularProgress size={20} />}
        sx={styles.submitButton}
      >
        {isRedirecting ? 'Redirecting...' : 'Submit'}
      </Button>
    </Div>
  );
};

export default LingoLiftLessonFooter;
